import { useSelector } from "react-redux";

import {
    AddBonusCardForm,
    AddBonusCardFormValues
} from "@/components/Account/AddBonusCard/partials/addForm";
import EditForm from "@/components/Account/AddBonusCard/partials/editForm";
import {
    failureMessage,
    removeFailureMessage,
    removeSuccessMessage,
    successMessage
} from "@/components/Account/AddBonusCard/partials/feedback";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import {
    APIStatus,
    updateFromData
} from "@/redux/modules/api/data/customer/customer.module";
import * as CustomerSelectors from "@/redux/modules/api/data/customer/customer.selectors";
import { getCustomer } from "@/redux/modules/api/data/customer/customer.selectors";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { TApiAction } from "@/redux/store.types";
import { noEmptyValueFormatter } from "@/utils/textUtils";

type Props = {
    addTitle: string;
    addText: string;
    editTitle: string;
    editText: string;
};

const AddBonusCard = ({ addTitle, addText, editTitle, editText }: Props) => {
    const { t } = useGranite();

    const dispatch = useThunkDispatch();
    const customer = useSelector(getCustomer);

    const isUpdating = useSelector(CustomerSelectors.isLoading);

    const bonusCardNumber =
        customer?.eBonusCardNumber || customer?.bonusCardNumber;

    const clearMessages = () => {
        clearMessageById("bonuscard-feedback-success");
        clearMessageById("bonuscard-feedback-failure");
    };

    const submitForm = (values: AddBonusCardFormValues) => {
        if (!customer) {
            return;
        }

        clearMessages();

        dispatch(
            updateFromData({
                ...customer,
                bonusCardNumber: values.bonusCardNumber
            })
        ).then((data) => {
            if (data.type === APIStatus.SUCCESS) {
                broadcastMessage(successMessage());
            } else {
                broadcastMessage(
                    failureMessage(data.payload.error?.response?.data)
                );
            }
        });
    };

    const removeBonusCard = () => {
        if (!customer) {
            return;
        }

        clearMessages();
        dispatch(
            updateFromData({
                ...customer,
                bonusCardNumber: null
            })
        ).then((data: TApiAction) => {
            if (data.type === APIStatus.SUCCESS) {
                broadcastMessage(removeSuccessMessage());
            } else {
                broadcastMessage(removeFailureMessage());
            }
        });
    };

    return (
        <>
            {!!customer && (
                <div className={"mb-5"}>
                    {!bonusCardNumber && (
                        <AddBonusCardForm
                            title={addTitle}
                            text={addText}
                            customer={customer}
                            onSubmit={submitForm}
                            isLoading={isUpdating}
                        />
                    )}
                    {customer.eBonusCardNumber && (
                        <Row>
                            <Col
                                xs={6}
                                md={3}
                            >
                                <p>{t("profileDetails.label.bonuscard")}</p>
                            </Col>
                            <Col>
                                <p>
                                    {noEmptyValueFormatter(
                                        customer.eBonusCardNumber,
                                        customer.eBonusCardNumber
                                    )}
                                </p>
                            </Col>
                        </Row>
                    )}
                    {!customer.eBonusCardNumber && customer.bonusCardNumber && (
                        <EditForm
                            title={editTitle}
                            text={editText}
                            customer={customer}
                            removeBonusCard={removeBonusCard}
                            isLoading={isUpdating}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export { AddBonusCard };
